import React, { useEffect, useRef, useState } from "react";
import i18n from "i18n";
import { useTranslation } from "react-i18next";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { setTimeout } from "timers";

import { FilteringType, IDateValue, IFilterDateInputProps } from "../interfaces/filter-interfaces";
import { FilterDateTypePopup } from "../components/pop-ups/filter-date-popup";
import { formatDate } from "../../../utils/formatDate";
import { memoFilter } from "./memoFilter";

import "../../custom-modal-elements/custom-input-with-state.styles.scss";

// table header filter component, you send it in the column as filterBody prop
// eg. (filterBody: (inputData: IPrimeFilterData) => <MemoFilterDateRangeInput inputData={inputData} />)
export const MemoFilterDateRangeInput = memoFilter((props: IFilterDateInputProps) => {
    const { isFilterDisabled: isFilterDisabled, column } = props.inputData;

    if (isFilterDisabled) return <DisabledFilterDateRangeInput field={column.field} />;

    return <FilterDateRangeInput {...props} />;
});

export const FilterDateRangeInput = ({ inputData, placeholder }: IFilterDateInputProps) => {
    const { t } = useTranslation();
    const calendarRef = useRef<any>(null);
    const inputRef = useRef<HTMLDivElement>(null);

    const [filterType, setFilterType] = useState<FilteringType>("__range");

    const { column, onCalendarClose, onDateFilterChange, onFilterChange, filter, tableRef } = inputData;

    useEffect(() => {
        if (filter?.filterType && filterType != filter?.filterType) setFilterType(filter?.filterType);
    }, [filter?.filterType]);

    const selectTemplate = (e) => {
        let { options, value } = e;
        options = options.map((o) => ({ ...o, label: t(o.label) }));

        return (
            <Dropdown
                className="prime-calendar-select"
                value={value}
                options={options}
                onChange={(event) => e.onChange(event.originalEvent, event.value)}
                style={{ lineHeight: 1 }}
            />
        );
    };

    const getRangeValue = (): (Date | null)[] => {
        if (typeof filter?.value == "string" || typeof filter?.value == "number" || !filter?.value) return [];
        const _value = filter?.value;

        if (_value?.[1] != null) return [new Date(_value?.[0]), new Date(_value?.[1])];
        else return [new Date(_value?.[0]), null];
    };

    const getDataPicker = () => {
        switch (filterType) {
            case "__range":
                return (
                    <Calendar
                        ref={calendarRef}
                        selectionMode="range"
                        id="range"
                        readOnlyInput
                        locale={i18n.languages[0]}
                        key={`prime-filter-input-${column.field}`}
                        name={column.filterField}
                        value={getRangeValue() as Date[]}
                        placeholder={placeholder ? t(placeholder) : `${t("Filter")} ${column.header}`}
                        onHide={() => onCalendarClose(column.field)}
                        onChange={(e) => {
                            onDateFilterChange(
                                {
                                    filterField: column.filterField,
                                    field: column.field,
                                    value: [formatDate(e.value?.[0]), formatDate(e.value?.[1])],
                                },
                                filterType
                            );
                            if (e.value?.[1] && calendarRef.current) {
                                setTimeout(() => {
                                    calendarRef.current?.hideOverlay();
                                }, 0);
                            }
                        }}
                        yearRange="1900:2200"
                        dateFormat="dd/mm/yy"
                        monthNavigator
                        yearNavigator
                        monthNavigatorTemplate={selectTemplate}
                        yearNavigatorTemplate={selectTemplate}
                    />
                );
            default:
                //clear filterValue
                return (
                    <>
                        <Calendar
                            readOnlyInput
                            key={`prime-filter-input-${column.field}`}
                            name={column.filterField}
                            locale={i18n.languages[0]}
                            value={new Date(typeof filter?.value == "string" ? filter?.value : "")}
                            placeholder={placeholder ? t(placeholder) : `${t("Filter")} ${column.header}`}
                            onHide={() => onCalendarClose(column.field)}
                            onChange={(e) =>
                                onDateFilterChange(
                                    { filterField: column.filterField, field: column.field, value: formatDate(e.value) },
                                    filterType
                                )
                            }
                            yearRange="1900:2200"
                            dateFormat="dd/mm/yy"
                            monthNavigator
                            yearNavigator
                            monthNavigatorTemplate={selectTemplate}
                            yearNavigatorTemplate={selectTemplate}
                        />
                    </>
                );
        }
    };
    return (
        <div className="table-filter" key={`prime-filter-input-${column.field}-container`} ref={inputRef}>
            {getDataPicker()}
            {typeof filter?.value != "undefined" && (
                <span
                    onClick={() =>
                        filter && onFilterChange({ value: undefined, filterField: column.filterField, field: column.field }, "clear")
                    }
                    className="pi pi-times state-input-clear-button"
                />
            )}
            <FilterDateTypePopup
                filterDateType={filterType}
                handleFilterDateTypeChange={setFilterType}
                tableRef={tableRef || inputRef}
                inputRef={inputRef}
                t={t}
            />
        </div>
    );
};

const DisabledFilterDateRangeInput = ({ field }) => {
    const { t } = useTranslation();

    return (
        <div className="table-filter" key={`prime-filter-input-${field}-container-disabled`}>
            <Calendar
                key={`prime-filter-input-${field}-disabled`}
                placeholder={t("Advanced filter...")}
                locale={i18n.languages[0]}
                readOnlyInput
                name={field}
                disabled
            />
        </div>
    );
};
